import plusIcon from 'public/static/images/add-icon.svg';
import minusIcon from 'public/static/images/minus-icon.svg';
import { useContext, useState, useEffect } from 'react';
import toggleMessageEvent from 'lib/Tracking/LeadForm/ToggleMessageEvent';
import toggleMarketingEvent from 'lib/Tracking/LeadForm/ToggleMarketingEvent';
import Checkbox from 'components/Shared/Checkbox';
import VdpEventContext from 'contexts/VdpEventContext';
import tapEvent from 'lib/Tracking/LeadForm/TapEvent';
import FormField from 'components/Shared/FormField';
import { Field, FieldProps } from 'formik';
import { useTranslation } from 'react-i18next';
import useEvents from 'lib/hooks/useEvents';
import Spacer from 'components/Shared/Spacer';
import { colors, screen, fonts } from 'components/styles/variables';
import css from 'styled-jsx/css';
import LeadFormTrackingContext from 'components/Vdp/LeadFormTrackingContext';
import emailValidationEvent from 'lib/Tracking/LeadForm/EmailValidationEvent';
import nameValidationEvent from 'lib/Tracking/LeadForm/NameValidationEvent';
import phoneValidationEvent from 'lib/Tracking/LeadForm/PhoneValidationEvent';
import { LeadSubmission } from 'lib/hooks/useSubmitted';
import useOptimizelyDecisions from 'lib/hooks/useOptimizelyDecisions';
import { trackContactFormClick } from 'lib/GoogleAdWords';
import PriceRequest from './PriceRequest';

export const textAreaStyles = css.resolve`
  .textArea {
    height: 128px;
    box-sizing: border-box;
    border: 1px solid ${colors.G3};
    caret-color: ${colors.ALR1};
    font-size: 18px;
    color: ${colors.BLACK};
    border-radius: 4px;
    padding: 12px 16px;
    font-family: ${fonts.default};
    -webkit-appearance: none;
    resize: vertical;
    flex-shrink: 0;
  }

  .textArea:focus {
    border: 2px solid ${colors.BLACK};
    outline: none;
  }
`;

interface FieldSetProps {
  showZipCode?: boolean;
  showAddress?: boolean;
  showMessage?: boolean;
  showAlerts?: boolean;
  lockEmail?: boolean;
  isSubmitted: boolean;
  formInputClassName?: string;
  marginClassName?: string;
  leadSubmission: LeadSubmission;
  vehiclePrice?: number;
  vin: string;
}

enum MessageClasses {
  plus = 'plus',
  minus = 'minus',
  noBorder = 'no-border',
}

export default function FieldSet({
  isSubmitted,
  formInputClassName,
  showZipCode,
  showAddress,
  showMessage = true,
  showAlerts = true,
  lockEmail = false,
  marginClassName,
  leadSubmission,
  vehiclePrice,
  vin,
}: FieldSetProps): JSX.Element {
  const { trackingParams } = useContext(VdpEventContext);
  const eventData = useContext(LeadFormTrackingContext);
  const messageState = Boolean(isSubmitted && leadSubmission.message);
  const [messageOpen, setMessageOpen] = useState(messageState);
  const [inputFocused, setInputFocused] = useState(false);

  useEffect(() => {
    setMessageOpen(messageState);
  }, [messageState]);

  const { t } = useTranslation();
  const logger = useEvents();

  const nameFieldValue = isSubmitted ? { value: leadSubmission.name } : {};
  const emailFieldValue = isSubmitted ? { value: leadSubmission.email } : {};
  const phoneFieldValue = isSubmitted ? { value: leadSubmission.phone } : {};
  const zipFieldValue = isSubmitted ? { value: leadSubmission.zip } : {};
  const addressFieldValue = isSubmitted ? { value: leadSubmission.address } : {};
  const messageFieldValue = isSubmitted ? { value: leadSubmission.message } : {};

  function handleOnClick(): void {
    if (!inputFocused) {
      setInputFocused(true);
      trackContactFormClick(vin);
    }
  }

  const { getDecision } = useOptimizelyDecisions();
  const isOnlineToOffline = getDecision('onlineToOfflineV1');

  return (
    <>
      <Field name="name" autocomplete="on">
        {(props: FieldProps): JSX.Element => {
          const { field, form } = props;
          const validationError = t(form.errors.name as string);
          const isTouched = form.touched.name;
          return (
            <FormField
              data-testid="name"
              disabled={isSubmitted}
              type="text"
              className={formInputClassName}
              error={validationError}
              showValidationIcon={(isTouched as boolean) || isSubmitted}
              label={t('leadForm.name')}
              {...field}
              {...nameFieldValue}
              onBlur={field.onBlur}
              onClick={(): void => {
                handleOnClick();
                logger.log(tapEvent({ action: 'name_field', trackingParams, ...eventData }));
              }}
              onValidation={(): void => {
                if (!isSubmitted) {
                  logger.log(
                    nameValidationEvent({ validationError, trackingParams, ...eventData })
                  );
                }
              }}
            />
          );
        }}
      </Field>
      <Spacer size="29px" />
      <Field name="email" autocomplete="on">
        {(props: FieldProps): JSX.Element => {
          const { field, form } = props;
          const validationError = t(form.errors.email as string);
          const isTouched = form.touched.email;
          return (
            <FormField
              data-testid="email"
              disabled={isSubmitted}
              locked={lockEmail}
              type="email"
              className={formInputClassName}
              error={validationError}
              showValidationIcon={(isTouched as boolean) || isSubmitted}
              label={t('leadForm.emailAddress')}
              {...field}
              {...emailFieldValue}
              onBlur={field.onBlur}
              onClick={(): void => {
                handleOnClick();
                logger.log(tapEvent({ action: 'email_field', trackingParams, ...eventData }));
              }}
              onValidation={(): void => {
                if (!isSubmitted) {
                  logger.log(
                    emailValidationEvent({ validationError, trackingParams, ...eventData })
                  );
                }
              }}
            />
          );
        }}
      </Field>
      <Spacer size="29px" />
      <Field name="phone" autocomplete="on">
        {(props: FieldProps): JSX.Element => {
          const { field, form } = props;
          const validationError = t(form.errors.phone as string);
          const isTouched = form.touched.phone;
          return (
            <FormField
              data-testid="phone"
              disabled={isSubmitted}
              type="tel"
              error={validationError}
              className={formInputClassName}
              showValidationIcon={Boolean(
                (phoneFieldValue.value || field.value) && (isTouched || isSubmitted)
              )}
              label={t('leadForm.phoneNumber')}
              {...field}
              {...phoneFieldValue}
              onBlur={field.onBlur}
              onClick={(): void => {
                handleOnClick();
                logger.log(tapEvent({ action: 'phone_field', trackingParams, ...eventData }));
              }}
              onValidation={(): void => {
                if (!isSubmitted) {
                  logger.log(
                    phoneValidationEvent({ validationError, trackingParams, ...eventData })
                  );
                }
              }}
            />
          );
        }}
      </Field>
      {isOnlineToOffline && (
        <PriceRequest
          className={formInputClassName}
          vehiclePrice={vehiclePrice as number}
          disabled={leadSubmission.isSubmitted}
        />
      )}
      {showAddress && (
        <>
          <Spacer size="29px" />
          <Field name="address" autocomplete="on">
            {(props: FieldProps): JSX.Element => {
              const { field, form } = props;
              const validationError = t(form.errors.address as string);
              const isTouched = form.touched.address;
              return (
                <FormField
                  data-testid="address"
                  disabled={isSubmitted}
                  type="text"
                  className={formInputClassName}
                  error={validationError}
                  showValidationIcon={(isTouched as boolean) || isSubmitted}
                  label={t('leadForm.address')}
                  {...field}
                  {...addressFieldValue}
                />
              );
            }}
          </Field>
        </>
      )}
      {showZipCode && (
        <>
          <Spacer size="29px" />
          <Field name="zip" autocomplete="on">
            {(props: FieldProps): JSX.Element => {
              const { field, form } = props;
              const validationError = t(form.errors.zip as string);
              const isTouched = form.touched.zip;
              return (
                <FormField
                  data-testid="zip"
                  disabled={isSubmitted}
                  type="text"
                  className={formInputClassName}
                  error={validationError}
                  showValidationIcon={(isTouched as boolean) || isSubmitted}
                  label={t('leadForm.zipCode')}
                  {...field}
                  {...zipFieldValue}
                />
              );
            }}
          </Field>
        </>
      )}
      {showMessage && (
        <>
          <Spacer size="16px" />
          <button
            data-testid="message"
            type="button"
            onClick={(): void => {
              logger.log(toggleMessageEvent({ isOpen: messageOpen, trackingParams }));
              setMessageOpen(!messageOpen);
            }}
            className={`message ${messageOpen ? MessageClasses.noBorder : ''} ${marginClassName}`}
            disabled={isSubmitted}
          >
            <div className={messageOpen ? MessageClasses.minus : MessageClasses.plus} />
            <div className="message-text">
              {messageOpen ? t('leadForm.collapseMessage') : t('leadForm.addMessage')}
            </div>
          </button>
        </>
      )}
      {showMessage && messageOpen && (
        <Field
          as="textarea"
          autoFocus={true}
          name="message"
          data-testid="message-input"
          className={`textArea ${textAreaStyles.className} ${marginClassName}`}
          disabled={isSubmitted}
          {...messageFieldValue}
        />
      )}
      {showAlerts && (
        <>
          <Spacer size="8px" smSize="16px" />
          <div className={`send-alerts-container ${marginClassName}`}>
            <Field name="sendAlerts">
              {(props: FieldProps): JSX.Element => {
                const { field } = props;
                return (
                  <Checkbox
                    data-testid="checkbox"
                    disabled={isSubmitted}
                    checked={isSubmitted ? leadSubmission.sendAlerts : field.value}
                    {...field}
                    onClick={(): void => {
                      logger.log(toggleMarketingEvent({ isOptedIn: field.value, trackingParams }));
                    }}
                  />
                );
              }}
            </Field>
            <div className="send-alerts-text">{t('leadForm.send')}</div>
          </div>
        </>
      )}
      <style jsx>{`
        .message:focus {
          outline: none;
        }

        .message-text {
          font-size: 16px;
          font-weight: 400;
          color: ${colors.G4};
        }

        .margin {
          margin: 0 16px;
        }

        @media (min-width: ${screen.smMin}) {
          .message-text {
            font-size: 18px;
          }
        }

        .message {
          display: flex;
          align-items: center;
          padding: 16px 0px;
          background: transparent;
          border: none;
          border-bottom: 1px solid ${colors.G2};
          font-family: ${fonts.default};
          cursor: pointer;
          flex-shrink: 0;
        }

        .${MessageClasses.plus} {
          width: 24px;
          height: 24px;
          margin-right: 12px;
          background-image: url(${plusIcon});
          background-size: cover;
        }

        .${MessageClasses.minus} {
          width: 24px;
          height: 24px;
          margin-right: 12px;
          background-image: url(${minusIcon});
          background-size: cover;
        }

        .${MessageClasses.noBorder} {
          border-bottom: none;
        }

        .send-alerts-container {
          display: flex;
          align-items: center;
          margin-top: 14px;
          margin-bottom: 2px;
        }

        .send-alerts-text {
          margin-left: 12px;
          font-size: 12px;
          font-weight: 400;
          color: ${colors.BLACK};
        }

        @media (min-width: ${screen.smMin}) {
          .send-alerts-text {
            font-size: 16px;
            margin-left: 24px;
          }
        }

        @media (min-width: ${screen.mdMin}) {
          .send-alerts-text {
            margin-left: 12px;
          }
        }
      `}</style>
      {textAreaStyles.styles}
    </>
  );
}
